import clsx from "clsx";
import { memo, useEffect, useState } from "react";
import ChipsBox, { CategoryItems } from "../../../../app/components/chipsBox/ChipsBox";
import InputFilter from "../../../../app/components/filter-products/InputFilter";
import SelectDropdown from "../../../../app/components/filter-products/SelectDropdown";
import { COLORS } from "../../../../core/consts/redesign/commonRedesign";
import { ArrowDown, ArrowUp, Settings } from "../../../../core/consts/redesign/icons";
import useIsMobile from "../../../../core/hooks/useIsMobile";
import { ClientTypes } from "../../../../core/types/agentTypes";
import classes from "./ClientsFilter.module.scss";

type PropsType = {
  innList?: string[] | undefined;
  searchInn?: string;
  setSearchInn?: (prev: string | ((prev: string) => string)) => void;
  companyNameList?: string[] | undefined;
  searchCompanyName?: string;
  setSearchCompanyName?: (prev: string | ((prev: string) => string)) => void;
  regionsList?: string[] | undefined;
  setSearchRegion?: (value: string) => void;
  searchRegion?: string;
  setSearchClientType: (value: string) => void;
  searchClientType: string;
  onClear: () => void;
  onClearInn: () => void;
  onClearCompanyName: () => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  handleFilterClick?: () => void;
  setIsFiltersChanged?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ClientsFilter = ({
  innList,
  searchInn,
  setSearchInn,
  companyNameList,
  setSearchCompanyName,
  searchCompanyName,
  regionsList,
  setSearchRegion,
  searchRegion,
  setSearchClientType,
  searchClientType,
  onClear,
  onClearInn,
  onClearCompanyName,
  setCurrentPage,
  handleFilterClick,
  setIsFiltersChanged
}: PropsType) => {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [showResetAll, setShowResetAll] = useState(false);

  useEffect(() => {
    const hasFilters = (
      (searchRegion?.length || 0) > 0 ||
      (searchClientType?.length || 0) > 0
    );
    setShowResetAll(hasFilters);
  }, [searchCompanyName, searchRegion, searchClientType, searchInn]);

  const accordionToggle = () => {
    setExpanded(!expanded);
  };

  const handleInnChange = (selectedInn: string) => {
    setCurrentPage(1);
    setSearchInn && setSearchInn(selectedInn);
  };

  const handleCompanyNameChange = (selectedCompanyName: string) => {
    setCurrentPage(1);
    setSearchCompanyName && setSearchCompanyName(selectedCompanyName);
  };

  const handleClientTypeChange = (selectedValue: string) => {
    setCurrentPage(1);
    setSearchClientType && setSearchClientType(selectedValue);
  };

  const handleRegionChange = (selectedValue: string) => {
    setCurrentPage(1);
    setSearchRegion && setSearchRegion(selectedValue)
  }

  const handleDeleteRegion = () => {
    setCurrentPage(1);
    setSearchRegion && setSearchRegion('');
  };

  const handleDeleteClientType = () => {
    setCurrentPage(1);
    setSearchClientType && setSearchClientType('');
  };

  const categories: CategoryItems[] = [
    {
      label: 'Region',
      items: searchRegion || [],
      onDeleteItem: handleDeleteRegion,
    },
    {
      label: 'Client Type',
      items: searchClientType || [],
      onDeleteItem: handleDeleteClientType,
    },
  ];

  return (
    <div className={classes.searchWrap} onClick={handleFilterClick}>
      <div className={classes.form}>
        {isMobile ? (
          <>
            <InputFilter
              onChange={handleInnChange}
              onClear={onClearInn}
              label={"Поиск по инн"}
              placeholder={"Начните вводить ИНН для поиска"}
              options={innList || []}
              id='inn_filter'
              initialFilter={searchInn}
            />
            {!expanded &&
              <div
                onClick={accordionToggle}
                className={clsx(classes.rowFlex, classes.filterHover)}
                style={{ margin: 0 }}>
                <div className={classes.filterFlex}>
                  <Settings color={COLORS.brand_blue} />
                  <span className={classes.filterText}>показать фильтры</span>
                </div>
                <ArrowDown color={COLORS.brand_blue} />
              </div>}

            <div className={!expanded ? classes.accordion : classes.accordionExpanded}>
              <InputFilter
                id='companies'
                onClear={onClearCompanyName}
                onChange={handleCompanyNameChange}
                label={"Наименование клиента"}
                options={innList || []}
                placeholder={"Начните вводить наименование"}
                initialFilter={searchCompanyName}
                style={{ marginBottom: 0 }}
              />
              <SelectDropdown
                id='region'
                value={searchRegion || ''}
                label={"Регион"}
                onChange={handleRegionChange}
                placeholder={"Все регионы"}
                options={regionsList || []}
              />
              <SelectDropdown
                id='clientType'
                value={searchClientType || ''}
                label={"Тип клиента"}
                onChange={handleClientTypeChange}
                placeholder={"Все типы клиента"}
                options={Object.values(ClientTypes)}
                setIsFiltersChanged={setIsFiltersChanged}
              />
            </div>
            {expanded && (
              <div onClick={accordionToggle} className={clsx(classes.rowFlex, classes.filterHover)}>
                <div className={classes.filterFlex}>
                  <Settings color={COLORS.brand_blue} />
                  <span className={classes.filterText}>скрыть фильтры</span>
                </div>
                <ArrowUp color={COLORS.brand_blue} />
              </div>
            )}
          </>
        ) : (
          <>
            <div className={classes.rowFlex}>
              <InputFilter
                id='inn'
                onClear={onClearInn}
                onChange={handleInnChange}
                label={"Поиск по инн клиента"}
                options={innList || []}
                placeholder={"Начните вводить ИНН для поиска"}
                initialFilter={searchInn}
              />
              <div className={classes.line_y} />
              <InputFilter
                id='companies'
                onClear={onClearCompanyName}
                onChange={handleCompanyNameChange}
                label={"Наименование клиента"}
                options={companyNameList || []}
                placeholder={"Начните вводить наименование"}
                initialFilter={searchCompanyName}
              />
            </div>

            <div className={classes.rowFlex}>
              <>
                <SelectDropdown
                  id='region'
                  noBorder
                  value={searchRegion || ''}
                  label={"Регион"}
                  onChange={handleRegionChange}
                  placeholder={"Все регионы"}
                  options={regionsList || []}
                />
                <div className={classes.line_y} />
                <SelectDropdown
                  id='clientType'
                  noBorder
                  value={searchClientType || ''}
                  label={"Тип клиента"}
                  onChange={handleClientTypeChange}
                  placeholder={"Все типы клиента"}
                  options={Object.values(ClientTypes)}
                  setIsFiltersChanged={setIsFiltersChanged}
                />
              </>
            </div>
          </>)}
      </div>
      <ChipsBox
        id='filter-clients'
        categories={categories}
        showResetAll={showResetAll}
        onResetAll={onClear}
      />
    </div>
  )
}

export default memo(ClientsFilter)
